import { toast } from "@/hooks/use-toast";
import type { ToastActionElement } from "@/components/ui/toast";
import {
  resolveFriendlyError,
  type ApiErrorLike,
  type FriendlyError,
} from "./errorCatalog";
import { audienceOf, getCurrentUser, type CurrentUserSnapshot } from "@/lib/auth/currentUser";

export interface ShowApiErrorOptions {
  /** Title/description used when nothing in the catalog/patterns matches. */
  fallback?: Partial<FriendlyError>;
  /**
   * Viewer whose role tailors the copy/CTA. Defaults to the signed-in user
   * (read from the module snapshot), so call sites get role-aware messages for
   * free. Pass explicitly only to override (e.g. tests).
   */
  user?: CurrentUserSnapshot | null;
  /** Optional toast action button (e.g. a "Report" / "Retry" action element). */
  action?: ToastActionElement;
  /** Optional toast duration in ms (defaults to the toast system default). */
  duration?: number;
  /**
   * Deprecated/no-op. Previously emailed with the "Report to Wenext" action,
   * which has been removed. Kept so existing call sites still type-check.
   */
  context?: Record<string, any>;
  /** Deprecated/no-op. The "Report to Wenext" action was removed. */
  report?: boolean;
}

/**
 * Translate any apiClient result / Error / string into a friendly destructive
 * toast. Use this instead of `toast({ description: res.error })` so users see
 * human copy instead of raw backend/Meta strings.
 *
 * Returns the resolved friendly error so callers can also use it inline
 * (e.g. set a field error or a banner) without re-resolving.
 *
 *   const res = await someApi.doThing(...)
 *   if (!res.success) return showApiError(res, { fallback: { title: "Couldn't save" } })
 */
export function showApiError(
  input: ApiErrorLike | Error | string | null | undefined,
  options: ShowApiErrorOptions = {},
): FriendlyError & { raw: string | null } {
  const { fallback, action, duration } = options;
  const user = options.user !== undefined ? options.user : getCurrentUser();
  const friendly = resolveFriendlyError(input, fallback, audienceOf(user));

  toast({
    title: friendly.title,
    description: friendly.description,
    variant: "destructive",
    ...(action ? { action } : {}),
    ...(duration ? { duration } : {}),
  });

  return friendly;
}
